<template>
    <div ref="hcaptchaElementRef" />
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useHcaptchaReady } from '~/composables/create-hcaptcha'

const hcaptchaElementRef = ref<HTMLElement | null>(null)
const widgetId = ref<number | null>(null)
const hcaptchaReady = useHcaptchaReady()
const siteKey = '2dcc2f82-c310-4c84-bc98-96f07840bb44'

let pendingResolve: ((token: string) => void) | undefined
let pendingReject: ((error: Error) => void) | undefined

const emit = defineEmits<{
    (e: 'token', payload: string): void
    (e: 'challengeOpened'): void
}>()

onMounted(renderHcaptcha)
onBeforeUnmount(() => {
    rejectPending(new Error('hcaptcha-unmounted'))
})

watch(hcaptchaReady, renderHcaptcha)

function renderHcaptcha() {
    if (
        !hcaptchaReady.value ||
        widgetId.value !== null ||
        siteKey === '' ||
        hcaptchaElementRef.value === null ||
        window.hcaptcha === undefined
    ) {
        return
    }

    // Render invisibly so clicking "Send Verification Code" starts the
    // challenge only when hCaptcha decides one is needed for this visitor.
    widgetId.value = window.hcaptcha.render(hcaptchaElementRef.value, {
        sitekey: siteKey,
        size: 'invisible',
        callback: (token: string) => {
            emit('token', token)
            resolvePending(token)
        },
        'expired-callback': () => {
            emit('token', '')
            rejectPending(new Error('hcaptcha-expired'))
        },
        'error-callback': () => {
            emit('token', '')
            rejectPending(new Error('hcaptcha-error'))
        },
        'open-callback': () => {
            // This is telemetry only; the server still trusts only the verified
            // hCaptcha token and never this client-reported challenge state.
            emit('challengeOpened')
        },
        'close-callback': () => {
            // If the user dismisses an interactive challenge, unblock the
            // LoginModal so they can retry instead of waiting forever.
            emit('token', '')
            rejectPending(new Error('hcaptcha-closed'))
        },
        'chalexpired-callback': () => {
            // This timeout is for an open challenge UI, distinct from the token
            // expiry callback that can fire after a successful solve.
            emit('token', '')
            rejectPending(new Error('hcaptcha-challenge-expired'))
        },
        theme: 'light'
    })
}

function execute(): Promise<string> {
    renderHcaptcha()

    if (widgetId.value === null || window.hcaptcha === undefined) {
        return Promise.reject(new Error('hcaptcha-not-ready'))
    }

    return new Promise((resolve, reject) => {
        rejectPending(new Error('hcaptcha-restarted'))
        pendingResolve = resolve
        pendingReject = reject
        window.hcaptcha?.execute(widgetId.value as number)
    })
}

function reset() {
    // hCaptcha tokens are short-lived and single-use, so reset after failed
    // server attempts before allowing another send-code submission.
    if (widgetId.value !== null && window.hcaptcha !== undefined) {
        window.hcaptcha.reset(widgetId.value)
        emit('token', '')
    }
}

function resolvePending(token: string) {
    if (pendingResolve !== undefined) {
        pendingResolve(token)
    }
    clearPending()
}

function rejectPending(error: Error) {
    if (pendingReject !== undefined) {
        pendingReject(error)
    }
    clearPending()
}

function clearPending() {
    pendingResolve = undefined
    pendingReject = undefined
}

defineExpose({ execute, reset })
</script>
